// D.2 — THE shared clinic location filter.
//
// ONE session-level value, five consumers: the week calendar, the legend
// chips, the Pending Requests table, the sidebar pending list and the
// sidebar badge count. Every one of them reads this module; there is no
// second filter state anywhere.
//
// LOAD-BEARING: this filter narrows DISPLAY ONLY. It is never sent to the
// server and never changes a fetch's parameters — access scope stays the
// server's `user_locations` rule (`services/location_scope.py`,
// `clinic_portal_router.get_clinic_me`). Statically pinned.
//
// `null` means "all of my locations" (first-load default, and what you get
// back when the last selected chip is switched off).
import { useCallback, useEffect, useState } from "react";

export const LOCATION_FILTER_KEY = "clinic-location-filter";
export const LOCATION_FILTER_EVENT = "clinic-location-filter";

export function readLocationFilter() {
  try {
    const raw = sessionStorage.getItem(LOCATION_FILTER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch {
    return null;
  }
}

// Persists + broadcasts, so consumers mounted in other component trees
// (the sidebar lives in ClinicPortal, the table in ClinicPendingRequests)
// move together. Returns the normalized value.
export function writeLocationFilter(codes) {
  const next = Array.isArray(codes) && codes.length ? codes : null;
  try {
    if (next) sessionStorage.setItem(LOCATION_FILTER_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(LOCATION_FILTER_KEY);
  } catch {
    /* private-mode storage — the in-memory value still drives the session */
  }
  window.dispatchEvent(new CustomEvent(LOCATION_FILTER_EVENT, { detail: { codes: next } }));
  return next;
}

export function matchesLocationFilter(selected, code) {
  if (!selected || selected.length === 0) return true;
  return selected.includes(code || "");
}

// Chip click semantics: from "all", a click ISOLATES that location; more
// clicks add; switching the last one off returns to all.
export function nextLocationSelection(selected, code) {
  if (!selected || selected.length === 0) return [code];
  if (selected.includes(code)) {
    const rest = selected.filter((c) => c !== code);
    return rest.length ? rest : null;
  }
  return [...selected, code];
}

export function useLocationFilter() {
  const [selected, setSelected] = useState(readLocationFilter);
  useEffect(() => {
    const handler = (e) => setSelected(e.detail?.codes ?? null);
    window.addEventListener(LOCATION_FILTER_EVENT, handler);
    return () => window.removeEventListener(LOCATION_FILTER_EVENT, handler);
  }, []);
  const toggle = useCallback((code) => {
    setSelected((prev) => writeLocationFilter(nextLocationSelection(prev, code)));
  }, []);
  const showAll = useCallback(() => setSelected(writeLocationFilter(null)), []);
  return {
    selected,
    isAll: !selected,
    toggle,
    showAll,
    isOn: (code) => matchesLocationFilter(selected, code),
  };
}
